// responsive-table.component.ts
import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';

import { NgbDropdownModule, NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';

import type { Column, RowAction } from './responsive-table.model';

@Component({
  selector: 'app-responsive-table',
  standalone: true,
  imports: [CommonModule, NgbDropdownModule, NgbPaginationModule],
  templateUrl: './responsive-table.html',
  styleUrl: './responsive-table.scss',
})
export class ResponsiveTable<T> {
  readonly items = input.required<readonly T[]>();
  readonly columns = input.required<readonly Column<T>[]>();
  readonly actions = input.required<readonly RowAction<T>[]>();
  readonly showActions = input<boolean>(true);

  /** Max inline actions for desktop. If actions exceed this, we keep (maxInline - 1) inline and put the rest in dropdown. */
  readonly maxInlineActions = input<number>(3);

  readonly trackBy = input<(index: number, item: T) => unknown>((i) => i);

  readonly actionSelected = output<{ actionId: string; item: T }>();


  readonly paginated = input<boolean>(false);
  readonly page = input<number>(1);
  readonly pageSize = input<number>(10);
  readonly totalItems = input<number>(0);

  readonly pageChange = output<number>();
  readonly pageSizeChange = output<number>();

  onAction(actionId: string, item: T): void {
    this.actionSelected.emit({ actionId, item });
  }

  isHidden(action: RowAction<T>, item: T): boolean {
    return action.hidden ? action.hidden(item) : false;
  }

  isDisabled(action: RowAction<T>, item: T): boolean {
    return action.disabled ? action.disabled(item) : false;
  }

  alignClass(align?: 'start' | 'center' | 'end'): string {
    return align === 'center' ? 'text-center' : align === 'end' ? 'text-end' : 'text-start';
  }

  visibleActionsFor(item: T): RowAction<T>[] {
    return this.actions().filter((a) => !this.isHidden(a, item));
  }

  inlineActionsFor(item: T): RowAction<T>[] {
    const list = this.visibleActionsFor(item);
    const max = Math.max(0, this.maxInlineActions());
    if (list.length <= max) return list;

    // If we need dropdown, keep (max - 1) inline and dropdown the rest.
    return list.slice(0, Math.max(0, max - 1));
  }

  dropdownActionsFor(item: T): RowAction<T>[] {
    const list = this.visibleActionsFor(item);
    const max = Math.max(0, this.maxInlineActions());
    if (list.length <= max) return [];

    return list.slice(Math.max(0, max - 1));
  }

  hasDropdown(item: T): boolean {
    return this.dropdownActionsFor(item).length > 0;
  }
}
